import { useEffect, useRef, useState } from "react";
import supabase from "../services/supabaseClient";
import { getUnityBridgeCommands } from "../lib/unityCommandBridge";
import {
  setUnityWorldReadyHandler,
  setUnitySessionRestoredHandler,
  setUnitySessionStateHandler,
} from "../lib/unityEvents";

/**
 * React is the source of truth for the Unity camera position (KAN-189).
 *
 * Unity sends SessionState (on RequestSessionState, or by itself when the canvas loses focus).
 * We keep the payload exactly as received, save it per user in localStorage right away and in
 * user_profile.unity_session with a debounce, and hand it back once via RestoreSession after
 * the first WorldReady. The saved entry is { state, saved_at }; `state` is never edited.
 */

const LOCAL_KEY_PREFIX = "nq_unity_session:";
const POLL_MS = 7000;
const DB_DEBOUNCE_MS = 15000;

function readLocal(userId) {
  try {
    const raw = localStorage.getItem(LOCAL_KEY_PREFIX + userId);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeLocal(userId, entry) {
  try {
    localStorage.setItem(LOCAL_KEY_PREFIX + userId, JSON.stringify(entry));
  } catch {
    // storage full or blocked — the DB copy still covers it
  }
}

function savedAtMs(entry) {
  const ms = Date.parse(entry?.saved_at ?? "");
  return Number.isFinite(ms) ? ms : 0;
}

/** Newest of the localStorage and DB copies (another device may have saved later). */
function pickSavedState(localEntry, remoteEntry) {
  const candidates = [localEntry, remoteEntry].filter((e) => e?.state && typeof e.state === "object");
  if (!candidates.length) return null;
  candidates.sort((a, b) => savedAtMs(b) - savedAtMs(a));
  return candidates[0].state;
}

/**
 * @param {object}   opts
 * @param {string}   opts.userId
 * @param {object}   opts.remoteEntry   user_profile.unity_session for this user
 * @param {boolean}  opts.profileReady  true once the profile (and so remoteEntry) has loaded
 * @param {function} opts.onRestored    called with SessionRestored ({ mode, roomID, buildingID, fallback })
 */
export default function useUnitySession({ userId, remoteEntry, profileReady, onRestored, prepareRestore }) {
  const [restored, setRestored] = useState(false);
  /** User id we already sent RestoreSession for — restore only once per user. */
  const restoredForUserRef = useRef(null);
  /** WorldReady arrived before the profile loaded — restore as soon as it does. */
  const pendingWorldReadyRef = useRef(false);
  /** Ignore SessionState until Unity has placed the user — the loading camera is temporary. */
  const acceptStateRef = useRef(false);
  const latestEntryRef = useRef(null);
  const lastDbJsonRef = useRef(null);
  const dbTimerRef = useRef(null);

  const userIdRef = useRef(userId);
  userIdRef.current = userId;
  const remoteEntryRef = useRef(remoteEntry);
  remoteEntryRef.current = remoteEntry;
  const profileReadyRef = useRef(profileReady);
  profileReadyRef.current = profileReady;
  const onRestoredRef = useRef(onRestored);
  onRestoredRef.current = onRestored;
  const prepareRestoreRef = useRef(prepareRestore);
  prepareRestoreRef.current = prepareRestore;

  const writeDb = (forUserId) => {
    clearTimeout(dbTimerRef.current);
    dbTimerRef.current = null;
    const entry = latestEntryRef.current;
    if (!forUserId || !entry) return;
    const json = JSON.stringify(entry);
    if (json === lastDbJsonRef.current) return;
    lastDbJsonRef.current = json;
    supabase
      .from("user_profile")
      .update({ unity_session: entry })
      .eq("user_id", forUserId)
      .then(({ error }) => {
        if (error) {
          lastDbJsonRef.current = null;
          console.warn("[UnitySession] could not save position", error.message);
        }
      });
  };

  const sendRestore = () => {
    const id = userIdRef.current;
    if (!id || restoredForUserRef.current === id) return;
    const cmds = getUnityBridgeCommands();
    if (!cmds) return;
    restoredForUserRef.current = id;
    pendingWorldReadyRef.current = false;
    const state = pickSavedState(readLocal(id), remoteEntryRef.current);
    const prepare = prepareRestoreRef.current;
    Promise.resolve(state && prepare ? prepare(state) : state)
      .catch(() => state)
      .then((finalState) => {
        if (userIdRef.current !== id) return;
        console.info("[UnitySession] RestoreSession", finalState ?? "(default spot)");
        cmds.restoreSession(finalState);
      });
  };

  // New user: a fresh start. Flush the old user's pending save first (flow D).
  useEffect(() => {
    restoredForUserRef.current = null;
    acceptStateRef.current = false;
    pendingWorldReadyRef.current = false;
    latestEntryRef.current = null;
    lastDbJsonRef.current = null;
    setRestored(false);
    return () => writeDb(userId);
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (profileReady && pendingWorldReadyRef.current) sendRestore();
  }, [profileReady]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setUnityWorldReadyHandler(({ isNewUser }) => {
      const id = userIdRef.current;
      if (!id || restoredForUserRef.current === id) return;
      if (isNewUser) {
        // Unity places a new user at the default spot and sends SessionRestored itself
        // (possibly before this WorldReady) — nothing to send.
        restoredForUserRef.current = id;
        return;
      }
      if (!profileReadyRef.current) {
        pendingWorldReadyRef.current = true;
        return;
      }
      sendRestore();
    });

    // Also sent after a mid-session SyncWorldState / ClearWorld, when Unity puts the user back by itself.
    setUnitySessionRestoredHandler((payload) => {
      acceptStateRef.current = true;
      setRestored(true);
      onRestoredRef.current?.(payload);
    });

    setUnitySessionStateHandler((state) => {
      const id = userIdRef.current;
      if (!id || !acceptStateRef.current) return;
      const entry = { state, saved_at: new Date().toISOString() };
      latestEntryRef.current = entry;
      writeLocal(id, entry);
      if (!dbTimerRef.current) {
        dbTimerRef.current = setTimeout(() => writeDb(id), DB_DEBOUNCE_MS);
      }
    });

    return () => {
      setUnityWorldReadyHandler(null);
      setUnitySessionRestoredHandler(null);
      setUnitySessionStateHandler(null);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Ask for the position every few seconds, and once more when the tab is hidden.
  useEffect(() => {
    if (!restored || !userId) return undefined;
    const request = () => getUnityBridgeCommands()?.requestSessionState();
    const interval = setInterval(request, POLL_MS);
    const onVisibilityChange = () => {
      if (document.visibilityState !== "hidden") return;
      request();
      writeDb(userId);
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [restored, userId]); // eslint-disable-line react-hooks/exhaustive-deps
}
